import { getStockCommentary } from "@/controllers/stock";
import { textColorState, ortexColorState, userDataState } from "@/lib/store";
import { Skeleton, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import SmallObfuscationBanner from "../obfuscation/SmallObfuscationBanner";
import { ReactMarkdown } from "react-markdown/lib/react-markdown";
import { markdownHeadingComponents } from "@/lib/markdownHeadings";
import remarkGfm from "remark-gfm";
import { isOrtexApp } from "@/lib/utils";
import { shortAsOfSentence } from "@/lib/stock-seo";

/**
 * What the API answers a signed-out visitor, for every stock. The overview starts from it rather than
 * a skeleton, so the server's HTML already holds the block's final text: it is the largest text on
 * a phone, so its paint was the page's LCP, and it waited for hydration plus this request. The
 * request still goes out and its answer replaces this one. Only while the reader is unknown or a
 * visitor: once a subscriber is known (navigating here, or their user request answering first) the
 * placeholder gives way to the skeleton, so they never see the paywall text.
 */
const VISITOR_COMMENTARY = "This information is hidden, to access ORTEX without restrictions, please sign up for a full subscription - ORTEX - Global Financial Analytics";

/**
 * What a visitor reads in the block instead, when the stock payload has its free headline numbers
 * (short interest, days to cover, cost to borrow: the ones the meta description quotes). Plain text,
 * not blurred and with no padlock over it: these figures are free on every stock page. The paywall
 * sentence above is left only for a stock without them.
 */
const visitorText = (stock): string => shortAsOfSentence(stock?.shortKeyData) || VISITOR_COMMENTARY;

const isKnownSubscriber = (userData): boolean => !!userData && userData.subscription_plan !== "ORTEX Visitor";

/**
 * Whether the block's text is already final: the free numbers, which a visitor gets from the server's
 * HTML and keeps when the API answers (free stocks included: a visitor stays locked on those). A stored
 * session is the one wrong guess while the user is unknown, and the held loader covers the page then
 * (src/lib/loaderHold.ts).
 */
export const hasFinalVisitorText = (stock, userData): boolean => !isKnownSubscriber(userData) && visitorText(stock) !== VISITOR_COMMENTARY;

// Module scope: an inline renderer is a new component type on every render, so each re-render
// (theme colours, the user arriving) unmounted and remounted the paragraph, and the browser counted
// the new node as a fresh paint of the page's largest text.
const markdownComponents = {
    ...markdownHeadingComponents,
    p: ({ node, ...props }) => <p style={{ whiteSpace: "pre-wrap", margin: 0 }} {...props} />,
};

// rehype-raw re-parses the whole tree with an HTML parser (parse5) so raw HTML in the text renders. Text
// without a tag has nothing for it to do, and that pass was most of the block's render time on a phone.
// The plugin and its parser (about 190 KB, a chunk of their own) load only for a text that has a tag:
// never for a visitor's, which is also the only text the server renders, so both sides agree.
const NO_REHYPE = [];
const REMARK_GFM = [remarkGfm];
let rehypeRawPlugins: unknown[] | null = null;
const loadRehypeRaw = (): Promise<unknown> =>
    import("rehype-raw").then(
        (module) => (rehypeRawPlugins = [module.default]),
        // Offline, or a deploy since the page loaded: the text is shown without its HTML.
        () => (rehypeRawPlugins = NO_REHYPE)
    );
const hasTag = (text: string | null): boolean => !!text && text.includes("<");

const StockCommentary = ({ stock, isCustomView = false }) => {
    const userData = useAtomValue(userDataState);
    const startAsVisitor = !isCustomView && (userData === undefined || userData?.subscription_plan === "ORTEX Visitor");
    const visitorPlaceholder = visitorText(stock);
    // The free numbers replace the paywall text: shown as they are, never blurred or locked.
    const freeNumbers = visitorPlaceholder !== VISITOR_COMMENTARY;
    const [stockCommentary, setStockCommentary] = useState<string | null>(startAsVisitor ? visitorPlaceholder : null);
    const [obfuscated, setObfuscated] = useState<boolean>(startAsVisitor && !freeNumbers);
    // The upgrade banner waits for the API's answer, as before: it is centred over the block and
    // resizes when its price arrives, which moved it (a layout shift) when drawn by the server.
    const [answered, setAnswered] = useState<boolean>(false);
    const [failed, setFailed] = useState<boolean>(false);
    const knownSubscriber = isKnownSubscriber(userData);
    const textColor = useAtomValue(textColorState);
    const ortexColor = useAtomValue(ortexColorState);

    useEffect(() => {
        const fetchCommentary = async () => {
            const { commentary, obfuscated } = await getStockCommentary(stock.exchangesymbol, stock.ticker);
            setAnswered(true);
            // A visitor's answer is the paywall text: keep the free numbers in its place.
            if (obfuscated && freeNumbers) {
                setObfuscated(false);
                return setStockCommentary(visitorPlaceholder);
            }
            setObfuscated(obfuscated);
            return commentary ? setStockCommentary(commentary) : setStockCommentary("No stock commentary available.");
        };

        // A failure keeps the visitor's answer when that is what is showing (a subscriber's is
        // replaced by the effect below).
        fetchCommentary().catch(() => {
            setFailed(true);
            setStockCommentary((shown) => (shown === visitorPlaceholder ? shown : "No stock commentary available."));
        });

        return () => {
            setStockCommentary(null);
            setObfuscated(false);
            setAnswered(false);
            setFailed(false);
        };
    }, [stock?.exchangesymbol, stock?.ticker]);

    // The visitor placeholder is never a subscriber's: drop it as soon as one is known and the API has
    // not answered for them yet.
    useEffect(() => {
        if (!knownSubscriber || answered) return;
        setObfuscated(false);
        setStockCommentary((shown) => (shown === visitorPlaceholder ? (failed ? "No stock commentary available." : null) : shown));
    }, [knownSubscriber, answered, failed]);

    // A text with HTML in it waits (on the skeleton) for the plugin that renders it.
    const needsRaw = hasTag(stockCommentary);
    const [, setRawLoaded] = useState(false);
    useEffect(() => {
        if (needsRaw && !rehypeRawPlugins) loadRehypeRaw().then(() => setRawLoaded(true));
    }, [needsRaw]);
    const textReady = !!stockCommentary && (!needsRaw || !!rehypeRawPlugins);

    // Parsed once per text. The block re-renders as its request and the user arrive (three or four times
    // while the page starts up) and for color changes; without this each render parsed the markdown again.
    const markdown = useMemo(
        () =>
            textReady ? (
                <ReactMarkdown remarkPlugins={REMARK_GFM} rehypePlugins={needsRaw ? (rehypeRawPlugins as never) : NO_REHYPE} linkTarget={isOrtexApp() ? "_self" : "_blank"} components={markdownComponents}>
                    {stockCommentary}
                </ReactMarkdown>
            ) : null,
        [stockCommentary, textReady, needsRaw]
    );

    return (
        <>
            {obfuscated && answered && <SmallObfuscationBanner />}
            {textReady ? (
                // A div, not body2's <p>: the markdown inside renders its own <p>, and a <p> in a <p> is
                // split by the HTML parser, so the server's markup failed to hydrate and was rebuilt
                // (repainted) on the client.
                <Typography
                    variant="body2"
                    component="div"
                    sx={{
                        whiteSpace: "pre-line",
                        ...(isCustomView ? { fontSize: "0.7rem" } : null),
                        color: textColor,
                        "& a": {
                            color: ortexColor,
                            textDecoration: "none",
                            "&:hover": {
                                textDecoration: "underline",
                            },
                        },
                    }}
                    className={obfuscated ? "obfuscated" : ""}
                >
                    {markdown}
                </Typography>
            ) : (
                <Skeleton variant="rounded" width="100%" height="100%" sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <Typography style={{ display: "flex", justifyContent: "center", alignItems: "center", visibility: "visible", color: textColor }}>Generating your data highlights...</Typography>
                </Skeleton>
            )}
        </>
    );
};

export default StockCommentary;
